import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  LocalShipping as LocalShippingIcon,
  Send as SendIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';

// --- Funciones puras fuera del componente (referencia estable) ---
const normalizarFechaISO = (value) => {
  if (!value) return '';

  if (typeof value === 'string') {
    const datePart = value.trim().slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) return datePart;
  }

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const obtenerFechaCierre = (cierre) => normalizarFechaISO(cierre?.fechaCierre || cierre?.fecha);

const formatDateLabel = (isoDate) => {
  if (!isoDate) return '';
  const parts = isoDate.split('-');
  if (parts.length !== 3) return '';
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const parseDateFilterInput = (value) => {
  const text = (value || '').trim();
  if (!text) return '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;

  const match = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return '';

  return `${match[3]}-${match[2]}-${match[1]}`;
};

const obtenerIdentificadorCierre = (cierre) => cierre.idCierre ?? cierre.rowKey;

const obtenerSistemaCierre = (cierre) => (cierre?.sistema || 'Web').toString().toUpperCase();

const obtenerIdDetalleParaEnvio = (cierre) => {
  const idWeb = cierre.idCajaEncabezado ?? cierre.idCierre ?? null;
  const idFox = cierre.idArqueo ?? null;
  const sistema = obtenerSistemaCierre(cierre);

  if (sistema === 'FOX') {
    return { idCajaEncabezadoArqueo: null, idArqueo: idFox };
  }

  return { idCajaEncabezadoArqueo: idWeb, idArqueo: null };
};

const obtenerClaveUnicaCierre = (cierre) => {
  const sistema = obtenerSistemaCierre(cierre);
  const ids = obtenerIdDetalleParaEnvio(cierre);
  const idReal = sistema === 'FOX' ? ids.idArqueo : ids.idCajaEncabezadoArqueo;

  if (idReal !== null && idReal !== undefined && String(idReal).trim() !== '') {
    return `${sistema}-ID-${idReal}`;
  }

  const codigo = (cierre?.codigoCierre || cierre?.codigo || '').toString().trim().toUpperCase();
  const fecha = obtenerFechaCierre(cierre);
  return `${sistema}-ALT-${codigo}-${fecha}`;
};

const deduplicarCierres = (cierres = []) => {
  const map = new Map();
  cierres.forEach((cierre) => {
    map.set(obtenerClaveUnicaCierre(cierre), cierre);
  });
  return Array.from(map.values());
};

const formatNumber = (val) =>
  new Intl.NumberFormat('es-HN', { style: 'currency', currency: 'HNL' }).format(val || 0);
// --- Fin funciones puras ---

const ModalNuevoPTV = ({ open, onClose, onGuardado, server, user, filtros }) => {
  const [loadingCierres, setLoadingCierres] = useState(false);
  const [loadingCuentas, setLoadingCuentas] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const guardandoRef = useRef(false);

  // Catálogos
  const [cierresRevisados, setCierresRevisados] = useState([]);
  const [cuentasBancarias, setCuentasBancarias] = useState([]);
  const [sucursales, setSucursales] = useState([]);

  // Selecciones
  const [cierresSeleccionados, setCierresSeleccionados] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState(null);
  const [cuentaBancariaSeleccionada, setCuentaBancariaSeleccionada] = useState(null);
  const [fechaCierreFiltro, setFechaCierreFiltro] = useState('');
  const [fechaCierreFiltroInput, setFechaCierreFiltroInput] = useState('');

  // Campos del formulario
  const [empresaServicio, setEmpresaServicio] = useState('');
  const [trazabilidad, setTrazabilidad] = useState('');
  const [fechaEnvio, setFechaEnvio] = useState(() => new Date().toISOString().split('T')[0]);
  const [observacion, setObservacion] = useState('');

  useEffect(() => {
    if (open) {
      cargarCuentasBancarias();
      cargarSucursales();
    }
  }, [open]);

  // Cuando cambia la sucursal seleccionada, recargar los cierres revisados
  useEffect(() => {
    if (sucursalSeleccionada) {
      cargarCierresRevisados(sucursalSeleccionada.idSucursal);
    } else {
      setCierresRevisados([]);
      setCierresSeleccionados([]);
      setFechaCierreFiltro('');
      setFechaCierreFiltroInput('');
    }
  }, [sucursalSeleccionada]);

  useEffect(() => {
    setFechaCierreFiltro('');
    setFechaCierreFiltroInput('');
  }, [cierresRevisados]);

  const cargarSucursales = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${server}/MgSucursales`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const arr = data.$values || data || [];
        setSucursales(arr);
        // Pre-seleccionar si viene un filtro
        if (filtros?.idSucursal) {
          const match = arr.find(s => s.idSucursal === filtros.idSucursal);
          if (match) setSucursalSeleccionada(match);
        }
      }
    } catch (err) {
      console.error('Error al cargar sucursales:', err);
    }
  };

  const cargarCuentasBancarias = async () => {
    setLoadingCuentas(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${server}/ChkBancosCuentasBancarias`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCuentasBancarias(data.$values || data || []);
      }
    } catch (err) {
      console.error('Error al cargar cuentas bancarias:', err);
    } finally {
      setLoadingCuentas(false);
    }
  };

  const cargarCierresRevisados = async (idSucursal) => {
    setLoadingCierres(true);
    setCierresRevisados([]);
    setCierresSeleccionados([]);
    setFechaCierreFiltro('');
    setFechaCierreFiltroInput('');
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${server}/TransporteValores/GetCierresRevisados?idSucursal=${idSucursal}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (!response.ok) throw new Error('Error al obtener cierres revisados');
      const result = await response.json();
      if (result.success) {
        const datos = result.datos?.$values || result.datos || [];
        const cierresNormalizados = datos.map((cierre, index) => {
          const fechaCierre = obtenerFechaCierre(cierre);
          const idCierre =
            cierre.idCierre ??
            cierre.idCajaEncabezado ??
            cierre.idArqueo ??
            cierre.id ??
            null;
          const codigoCierre = cierre.codigoCierre ?? cierre.codigo ?? '';
          const sistema = cierre.sistema ?? 'Web';

          return {
            ...cierre,
            idCierre,
            codigoCierre,
            usuario: cierre.usuario ?? cierre.ingresadoPor ?? '',
            efectivo: Number(cierre.efectivo ?? 0),
            sistema,
            fechaCierre,
            rowKey: `${sistema}-${idCierre ?? 'sin-id'}-${codigoCierre || 'sin-codigo'}-${fechaCierre || 'sin-fecha'}-${index}`
          };
        });

        const cierresSinDuplicados = deduplicarCierres(cierresNormalizados);
        setCierresRevisados(cierresSinDuplicados);
      } else {
        throw new Error(result.message || 'Error al cargar cierres');
      }
    } catch (err) {
      console.error(err);
      Swal.fire({ icon: 'error', title: 'Error', text: err.message });
    } finally {
      setLoadingCierres(false);
    }
  };

  const toggleCierre = (cierre) => {
    const cierreKey = obtenerClaveUnicaCierre(cierre);
    setCierresSeleccionados((prev) => {
      const existe = prev.some((c) => obtenerClaveUnicaCierre(c) === cierreKey);
      if (existe) return prev.filter((c) => obtenerClaveUnicaCierre(c) !== cierreKey);
      return [...prev, cierre];
    });
  };

  const toggleTodos = () => {
    const keysFiltrados = new Set(cierresFiltrados.map((cierre) => obtenerClaveUnicaCierre(cierre)));
    const todosFiltradosSeleccionados =
      cierresFiltrados.length > 0 &&
      cierresFiltrados.every((cierre) =>
        cierresSeleccionados.some(
          (seleccionado) => obtenerClaveUnicaCierre(seleccionado) === obtenerClaveUnicaCierre(cierre)
        )
      );

    if (todosFiltradosSeleccionados) {
      setCierresSeleccionados((prev) =>
        prev.filter((cierre) => !keysFiltrados.has(obtenerClaveUnicaCierre(cierre)))
      );
      return;
    }

    setCierresSeleccionados((prev) => {
      const seleccionadosMap = new Map(
        prev.map((cierre) => [obtenerClaveUnicaCierre(cierre), cierre])
      );
      cierresFiltrados.forEach((cierre) => {
        seleccionadosMap.set(obtenerClaveUnicaCierre(cierre), cierre);
      });
      return Array.from(seleccionadosMap.values());
    });
  };

  const totalEfectivoSeleccionado = useMemo(
    () => cierresSeleccionados.reduce((sum, c) => sum + (c.efectivo || 0), 0),
    [cierresSeleccionados]
  );

  const fechasCierreDisponibles = useMemo(() => {
    const fechasUnicas = Array.from(
      new Set(cierresRevisados.map((c) => c.fechaCierre).filter(Boolean))
    );
    return fechasUnicas.sort((a, b) => b.localeCompare(a));
  }, [cierresRevisados]);

  const cierresFiltrados = useMemo(() => {
    // Filtrar cierres con efectivo > 0
    const conEfectivo = cierresRevisados.filter((c) => c.efectivo > 0);
    if (!fechaCierreFiltro) return conEfectivo;
    return conEfectivo.filter((c) => c.fechaCierre === fechaCierreFiltro);
  }, [cierresRevisados, fechaCierreFiltro]);

  const totalFiltradosSeleccionados = useMemo(() =>
    cierresFiltrados.filter((cierre) =>
      cierresSeleccionados.some(
        (sel) => obtenerClaveUnicaCierre(sel) === obtenerClaveUnicaCierre(cierre)
      )
    ).length,
    [cierresFiltrados, cierresSeleccionados]
  );

  const handleGuardar = async () => {
    if (guardandoRef.current || guardando) return;

    // Validaciones
    if (!sucursalSeleccionada) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Seleccione una sucursal' });
      return;
    }
    if (cierresSeleccionados.length === 0) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Seleccione al menos un cierre' });
      return;
    }
    if (!cuentaBancariaSeleccionada) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Seleccione una cuenta bancaria' });
      return;
    }
    if (!trazabilidad.trim()) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Ingrese el número de trazabilidad' });
      return;
    }
    if (!empresaServicio.trim()) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Ingrese el nombre de la empresa de servicio' });
      return;
    }
    if (!fechaEnvio) {
      Swal.fire({ icon: 'warning', title: 'Validación', text: 'Ingrese la fecha de envío' });
      return;
    }

    const cierresSinDuplicados = deduplicarCierres(cierresSeleccionados);
    if (cierresSinDuplicados.length !== cierresSeleccionados.length) {
      setCierresSeleccionados(cierresSinDuplicados);
      Swal.fire({
        icon: 'warning',
        title: 'Cierres duplicados detectados',
        text: 'Se detectaron cierres repetidos en la selección. Revise la lista y guarde nuevamente.'
      });
      return;
    }

    const sistemasSeleccionados = Array.from(
      new Set(cierresSinDuplicados.map((c) => obtenerSistemaCierre(c)))
    );
    if (sistemasSeleccionados.length > 1) {
      Swal.fire({
        icon: 'warning',
        title: 'Validación',
        text: 'No se puede mezclar cierres Web y Fox en un mismo envío. Seleccione un solo sistema.'
      });
      return;
    }

    const cierreSinIdValido = cierresSinDuplicados.find((c) => {
      const ids = obtenerIdDetalleParaEnvio(c);
      const sistema = obtenerSistemaCierre(c);
      return sistema === 'FOX' ? !ids.idArqueo : !ids.idCajaEncabezadoArqueo;
    });
    if (cierreSinIdValido) {
      Swal.fire({
        icon: 'warning',
        title: 'Validación',
        text: `El cierre ${cierreSinIdValido.codigoCierre || ''} no tiene identificador válido para envío.`
      });
      return;
    }

    guardandoRef.current = true;
    setGuardando(true);
    try {
      const token = localStorage.getItem('token');
      const sistemaEnvio = sistemasSeleccionados[0] === 'FOX' ? 'Fox' : 'Web';
      const payload = {
        idCuentaBancaria: cuentaBancariaSeleccionada.idCuentaBancaria,
        idSucursal: sucursalSeleccionada.idSucursal,
        totalEfectivoEnvio: cierresSinDuplicados.reduce((sum, c) => sum + (c.efectivo || 0), 0),
        observacion: observacion.trim() || null,
        trazabilidad: trazabilidad.trim(),
        codigoInterno: null,
        fechaEnvio: new Date(fechaEnvio).toISOString(),
        fechaDeposito: null,
        fechaArqueo: null,
        empresaServicio: empresaServicio.trim(),
        sistema: sistemaEnvio,
        ingresadoPor: user?.codigoUsuario || null,
        modificadoPor: user?.codigoUsuario || null,
        modificacion: 1,
        transporteValoresDetalles: cierresSinDuplicados.map(c => {
          const ids = obtenerIdDetalleParaEnvio(c);
          return {
            idCajaEncabezadoArqueo: ids.idCajaEncabezadoArqueo,
            idArqueo: ids.idArqueo,
            valorEfectivo: c.efectivo
          };
        })
      };

      const response = await fetch(`${server}/TransporteValores/PTV-envio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || result.error || 'Error al guardar el envío');
      }

      Swal.fire({
        icon: 'success',
        title: 'Envío Registrado',
        text: result.message || 'El envío de protección de valores se registró correctamente',
        timer: 2500,
        showConfirmButton: false
      });

      onGuardado(result.data);
    } catch (err) {
      console.error('Error al guardar PTV:', err);
      Swal.fire({ icon: 'error', title: 'Error al Guardar', text: err.message });
    } finally {
      guardandoRef.current = false;
      setGuardando(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#1565c0', color: 'white' }}>
        <LocalShippingIcon />
        Nuevo Envío — Protección de Valores
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          {/* Sucursal */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={sucursales}
              getOptionLabel={(o) => o.nombreSucursal || ''}
              value={sucursalSeleccionada}
              onChange={(_, v) => setSucursalSeleccionada(v)}
              renderInput={(params) => <TextField {...params} label="Sucursal *" fullWidth />}
            />
          </Grid>

          {/* Empresa de servicio */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Empresa de Servicio *"
              fullWidth
              value={empresaServicio}
              onChange={(e) => setEmpresaServicio(e.target.value)}
              placeholder="Ej: Prosegur, Securitas..."
            />
          </Grid>

          {/* Cuenta bancaria */}
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={cuentasBancarias}
              getOptionLabel={(o) => o.cuentaBancaria ? `${o.nombreBanco ? o.nombreBanco + '-' : ''}${o.cuentaBancaria}${o.cuentaContableNew ? ` — ${o.cuentaContableNew}` : ''}` : ''}
              value={cuentaBancariaSeleccionada}
              onChange={(_, v) => setCuentaBancariaSeleccionada(v)}
              loading={loadingCuentas}
              renderOption={(props, o) => (
                <li {...props} key={o.idCuentaBancaria}>
                  <Box>
                    <Typography variant="body2">{o.cuentaBancaria}</Typography>
                    {o.cuentaContableNew && (
                      <Typography variant="caption" color="text.secondary">{o.cuentaContableNew}</Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => <TextField {...params} label="Cuenta Bancaria *" fullWidth />}
            />
          </Grid>

          {/* Trazabilidad */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Código de Trazabilidad (Bolsa) *"
              fullWidth
              value={trazabilidad}
              onChange={(e) => setTrazabilidad(e.target.value)}
              placeholder="Ej: TV-20260317-001"
            />
          </Grid>

          {/* Fecha Envío */}
          <Grid item xs={12} sm={6}>
            <TextField
              label="Fecha de Envío *"
              type="date"
              fullWidth
              value={fechaEnvio}
              onChange={(e) => setFechaEnvio(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={fechasCierreDisponibles}
              getOptionLabel={(option) => formatDateLabel(option)}
              isOptionEqualToValue={(option, value) => option === value}
              value={fechaCierreFiltro || null}
              inputValue={fechaCierreFiltroInput}
              onChange={(_, value) => {
                const filtro = value || '';
                setFechaCierreFiltro(filtro);
                setFechaCierreFiltroInput(filtro ? formatDateLabel(filtro) : '');
              }}
              onInputChange={(_, value) => {
                setFechaCierreFiltroInput(value);
                setFechaCierreFiltro(parseDateFilterInput(value));
              }}
              disabled={!sucursalSeleccionada || loadingCierres || fechasCierreDisponibles.length === 0}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filtrar cierres por fecha"
                  placeholder="Seleccione una fecha"
                  fullWidth
                />
              )}
            />
          </Grid>

          {/* Observación */}
          <Grid item xs={12}>
            <TextField
              label="Observaciones"
              fullWidth
              multiline
              rows={2}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Tabla de cierres revisados */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Cierres Disponibles (Revisados)
          </Typography>
          {cierresSeleccionados.length > 0 && (
            <Chip
              label={`Total seleccionado: ${formatNumber(totalEfectivoSeleccionado)}`}
              color="success"
              variant="outlined"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>

        {!sucursalSeleccionada && (
          <Alert severity="info">Seleccione una sucursal para ver los cierres disponibles.</Alert>
        )}

        {sucursalSeleccionada && loadingCierres && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={30} />
          </Box>
        )}

        {sucursalSeleccionada && !loadingCierres && cierresRevisados.length === 0 && (
          <Alert severity="warning">No hay cierres revisados disponibles para esta sucursal.</Alert>
        )}

        {sucursalSeleccionada && !loadingCierres && cierresRevisados.length > 0 && cierresFiltrados.length === 0 && (
          <Alert severity="info">No hay cierres revisados para la fecha seleccionada.</Alert>
        )}

        {sucursalSeleccionada && !loadingCierres && cierresFiltrados.length > 0 && (
          <TableContainer component={Paper} sx={{ maxHeight: 280 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={
                        totalFiltradosSeleccionados > 0 &&
                        totalFiltradosSeleccionados < cierresFiltrados.length
                      }
                      checked={
                        cierresFiltrados.length > 0 &&
                        totalFiltradosSeleccionados === cierresFiltrados.length
                      }
                      onChange={toggleTodos}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Cód. Cierre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Fecha Cierre</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Empleado / Usuario</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Efectivo del Cierre</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cierresFiltrados.map((cierre) => {
                  const seleccionado = cierresSeleccionados.some(
                    (c) => obtenerClaveUnicaCierre(c) === obtenerClaveUnicaCierre(cierre)
                  );
                  return (
                    <TableRow
                      key={cierre.rowKey}
                      hover
                      onClick={() => toggleCierre(cierre)}
                      sx={{ cursor: 'pointer', bgcolor: seleccionado ? '#e3f2fd' : 'inherit' }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={seleccionado}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleCierre(cierre)}
                        />
                      </TableCell>
                      <TableCell>{cierre.codigoCierre || '-'}</TableCell>
                      <TableCell>{formatDateLabel(cierre.fechaCierre) || '-'}</TableCell>
                      <TableCell>{cierre.usuario}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatNumber(cierre.efectivo)}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={guardando}>Cancelar</Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
          onClick={handleGuardar}
          disabled={guardando}
        >
          {guardando ? 'Guardando...' : 'Guardar Envío'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalNuevoPTV;
